/**
 * approvalsMapping.js — the ONE place a server payday/approval payload becomes Approvals props.
 *
 * **Why this is a module and not four lines inside the container.** The Approvals screen and the
 * backend describe the same week in different vocabularies, and two of the mismatches produce a
 * confident wrong number rather than a crash. A crash is visible. "3 of 0" is not.
 *
 * | the screen wants | the server sends | what happens if you just map it |
 * | --- | --- | --- |
 * | `kid.questsTotal` | nothing | `Approvals.jsx` renders `toFinite(k.questsTotal, 0)`, so a child with 3 done reads **"3 of 0"** |
 * | `payday.splits` — three PERCENTAGES (50/10/40) | `child.jars` — three AMOUNTS in currency | "7.25" lands in a column headed `%`. So a live split carries `amounts` and NO percentage fields, and the screen draws money (#581 L08/L09) |
 * | `payday.holds` — a named child being held back | `child.weekState`, since #580 F-08 slice 2 | before it existed: an invented accusation about a real child. Now a card appears ONLY for `incomplete` (see `holdsFrom`) |
 *
 * **So the rule here is: send what the server sent, and OMIT what it did not.** Not zero, not an
 * empty-looking default that renders as a number — omitted, so the screen can tell "none" from
 * "not known". `Approvals.jsx` was changed in the same commit to render the done count alone when
 * no total is known, which is the other half of that contract.
 *
 * `#30` (R7.2) owns this wiring. The three gaps above are `#29` (R7.1) work: the payday preview
 * has no concept of a quest TARGET, no jar percentages, and no hold list. They are named in the
 * delivery record rather than filled in with plausible numbers.
 */

import { dayNameFromPaydayPeriod, paydayKind } from './payday.js';

/** A person whose name the server did not send. Never "undefined", never a bare user id. */
const UNNAMED = 'A family member';
/** A quest whose name the server did not send. */
const UNTITLED = 'A quest';

/** `'7.25'`, `7.25` and `null` all have to become a number the screen can format. */
function money(value) {
  const n = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * A child's three jar amounts in whole cents, or null when any one cannot be read.
 *
 * The server's `give` jar is the screen's `share` jar. Cents, because comparing the sum against the
 * child's total in floating point would call a correct split broken.
 */
function jarCents(jars) {
  if (!jars || typeof jars !== 'object') return null;
  const out = {};
  for (const [from, to] of [['save', 'save'], ['give', 'share'], ['spend', 'spend']]) {
    const n = typeof jars[from] === 'number' ? jars[from] : Number.parseFloat(jars[from]);
    if (!Number.isFinite(n) || n < 0) return null;
    out[to] = Math.round(n * 100);
  }
  return out;
}

function count(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : 0;
}

function list(value) {
  return Array.isArray(value) ? value : [];
}

/**
 * A short local time for a queue row, or `undefined` when the server sent no timestamp.
 *
 * `undefined` on purpose: the screen shows the "when" chip only when there is one, and a row that
 * says "just now" about an unknown time is worse than a row that says nothing.
 */
function when(iso) {
  if (typeof iso !== 'string' || iso === '') return undefined;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return undefined;
  return at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/**
 * `GET /api/approvals` → the rows the parent reviews.
 *
 * A row with no `id` is DROPPED rather than rendered. The screen keys on that id and sends it back
 * in `POST /api/approvals/:id/approve`, so a row without one is a button that would act on nothing.
 */
export function toApprovalRows(payload) {
  return list(payload && payload.approvals)
    .filter((row) => row && row.id != null)
    .map((row) => {
      const at = when(row.completedAt);
      const mapped = {
        id: row.id,
        actor: row.displayName || UNNAMED,
        title: row.choreName || UNTITLED,
        amount: money(row.rewardAmount),
      };
      // Only present when the server sent a usable timestamp — see `when` above.
      if (at !== undefined) mapped.when = at;
      return mapped;
    });
}

/** Shared table/footer copy. Omitted phrases retain the weekly fixture default. */
export function totalLabelFor(periodPhrase = 'this week') {
  return periodPhrase === 'this week' ? 'Week total' : 'Period total';
}

/**
 * The "settles …" caption, from the family's REAL window rather than a guess.
 *
 * Returns `undefined` when there is no window to read, so the screen falls back to its own copy
 * instead of announcing a day nobody chose. `calendar_month` gets its own sentence, because
 * `dayNameFromPaydayPeriod` correctly answers `null` for it and "Settles null" is the failure that
 * `lib/payday.js` exists to prevent.
 *
 * Exported so Home's pending-money caption (`dashboardSummaryMapping.js`) says the payday in the
 * same words as this screen, #580 D2: Home told a Monday family "Settles at Friday payday".
 */
export function settleLabelFor(window) {
  if (!window || typeof window !== 'object') return undefined;
  const period = window.paydayPeriod;
  let kind;
  try {
    kind = paydayKind(period);
  } catch {
    // `paydayKind` throws on an unknown period ON PURPOSE, so a bad value cannot become Sunday.
    // A caption is not worth failing the page for, so it is simply absent.
    return undefined;
  }
  if (kind === 'monthly') return 'Settles at the end of the month';
  // #580 I8a / 4a3: ad hoc has no scheduled payday (`nextPaydayAt` is null), so there is no day
  // and no countdown. Its accounting weeks are bookkeeping, not a date money is due.
  if (kind === 'ad_hoc') return 'Paid whenever you choose · no set payday';
  return `Settles ${dayNameFromPaydayPeriod(period)}`;
}

/**
 * `GET /api/payday/preview` + `GET /api/payday/window` → the payday panel.
 *
 * Every field the server cannot answer is left OFF the object rather than defaulted. Read the
 * table at the top of this file before adding one back.
 */
export function toPaydayPanel(preview, window) {
  const children = list(preview && preview.children);

  const kids = children.map((child) => {
    const kid = {
      id: child && child.userId,
      name: (child && child.firstName) || UNNAMED,
      // `eligibleCount` is how many approved quests are waiting to be paid. It is NOT "quests done
      // this week", and there is deliberately no `questsTotal` beside it.
      questsDone: count(child && child.eligibleCount),
      earned: money(child && child.eligibleTotal),
      alreadySettled: !!(child && child.alreadySettled),
    };
    // Money approved in a week nobody settled. `payday.service.js` lists a child who has ONLY
    // carried-over money precisely so it is not invisible, and dropping it here would undo that:
    // the row would read "0 · $0.00" with no reason for the row to exist. Present only when the
    // server sent a non-zero one, by the omit-what-was-not-sent rule above.
    const carried = child && child.carriedOver;
    if (carried && count(carried.count) > 0) {
      kid.carriedOver = { count: count(carried.count), total: money(carried.total) };
    }
    return kid;
  });

  const settleable = kids.filter(isSettleable);

  const panel = {
    kids,
    // Two totals, because they answer two different questions and conflating them made the settle
    // button promise money it would not send.
    //
    //   `total`       — what the ROWS add up to. The table footer says "Week total", and a footer
    //                   that does not equal its own column is its own defect.
    //   `settleTotal` — what pressing Settle will actually pay. `settleableUserIds` excludes a
    //                   child who is already settled, so a panel total that includes them named a
    //                   larger figure on the button than the request carried, and the screen's own
    //                   receipt matcher then compared the settlement against the wrong number.
    total: kids.reduce((sum, kid) => sum + kid.earned, 0),
    settleTotal: settleable.reduce((sum, kid) => sum + kid.earned, 0),
    // #581 L08/L09: the per-child jar split, as the MONEY each jar gets on this payday. Only for a
    // child this settle will pay, and only when the three jars add up to what that child earned.
    splits: children.flatMap((child, i) => {
      const kid = kids[i];
      if (!isSettleable(kid)) return [];
      const cents = jarCents(child && child.jars);
      if (!cents || cents.save + cents.share + cents.spend !== Math.round(kid.earned * 100)) return [];
      return [{
        id: kid.id,
        name: kid.name,
        amounts: { save: cents.save / 100, share: cents.share / 100, spend: cents.spend / 100 },
      }];
    }),
    // #580 F-08: one hold card per child the SERVER says had an incomplete week. See `holdsFrom`.
    holds: holdsFrom(preview, children, kids),
    // #580 4b2: every open hold the server lists, from any week. See `heldFrom`.
    held: heldFrom(preview),
    currency: (preview && preview.currency) || 'USD',
  };

  const label = settleLabelFor(window);
  if (label !== undefined) panel.settleLabel = label;
  // A month or an unreadable window must not invent a weekly period. Known weekly and
  // ad hoc accounting windows keep the existing week wording and optional week number.
  if (label === undefined || window.paydayPeriod === 'calendar_month') panel.periodPhrase = 'this period';
  return panel;
}

/** The revision `GET /api/payday/preview` puts on each child row: 64 lowercase hex. */
const REVISION = /^[0-9a-f]{64}$/;

/**
 * #580 F-08 slice 3 — the children the hold card is shown for.
 *
 * ONLY a child whose `weekState` is exactly `incomplete`. `unknown` means the server could not
 * judge the week (it began before occurrences were recorded), and a card there would accuse a real
 * child of a week nobody measured; `complete` needs no decision. The card also needs everything
 * "Pay anyway" will send back: the child's own `previewRevision` and the preview's window. Without
 * either it could only fail, so it is not offered. And only for a child this payday would actually
 * pay — already settled, or nothing eligible, means there is no money to decide about.
 *
 * The counts are the server's `weekCounts` (completed of due), OMITTED when it sent none.
 */
function holdsFrom(preview, children, kids) {
  const period = preview && preview.period;
  const periodStart = period && typeof period.periodStart === 'string' ? period.periodStart : null;
  const periodEnd = period && typeof period.periodEnd === 'string' ? period.periodEnd : null;
  if (!periodStart || !periodEnd) return [];

  return children.flatMap((child, i) => {
    const kid = kids[i];
    if (!child || child.weekState !== 'incomplete' || !isSettleable(kid)) return [];
    // Money, not just a quest count (fix round 1, review M3): one approved CA$0.00 quest is
    // settleable by count, and the server refuses a Pay anyway with nothing to pay. Only here:
    // `isSettleable` also decides the Settle payload, which this must not change.
    if (!(kid.earned > 0)) return [];
    if (typeof child.previewRevision !== 'string' || !REVISION.test(child.previewRevision)) return [];
    const hold = {
      id: kid.id,
      name: kid.name,
      earned: kid.earned,
      periodStart,
      periodEnd,
      previewRevision: child.previewRevision,
      approvalIds: child.approvalIds,
      jars: child.jars,
      // #580 4b2: Wait is offered only while the server says holds are switched on. Absent (an older
      // server) is off. Ad hoc has no next payday, so the question must not promise one.
      canWait: preview.holdsEnabled === true,
      adHoc: period.paydayPeriod === 'ad_hoc',
    };
    const counts = child.weekCounts;
    if (counts && Number.isFinite(Number(counts.due)) && Number.isFinite(Number(counts.completed))) {
      hold.questsDone = count(counts.completed);
      hold.questsTotal = count(counts.due);
    }
    return [hold];
  });
}

/**
 * #580 F-08 slice 4b2 — the money kept for later: every OPEN hold `GET /api/payday/preview` lists,
 * earlier weeks included, because held money is in no payable row (4b1a review M2).
 *
 * `overdue` is the server's `state`: past its deadline and still open, so still owed and NOT paid.
 * Unreadable amounts and local dates stay visibly unavailable: invalid details are not evidence
 * that the hold disappeared. `nextPaydayAt` is not carried; ad hoc has no scheduled payday.
 */
function heldFrom(preview) {
  return list(preview && preview.holds).flatMap((h) => {
    if (!h || !Number.isSafeInteger(h.holdId) || h.userId == null) return [];
    if (h.state !== 'waiting' && h.state !== 'overdue') return [];
    const total = money(h.total);
    const entry = {
      holdId: h.holdId,
      id: h.userId,
      currency: h.currency,
      periodStart: h.periodStart,
      periodEnd: h.periodEnd,
      name: (typeof h.firstName === 'string' && h.firstName) || UNNAMED,
      total: total > 0 ? total : null,
      releaseAt: h.releaseAt,
      timezone: h.timezone,
      overdue: h.state === 'overdue',
      attempts: count(h.releaseAttempts),
    };
    if (typeof h.nextAttemptAt === 'string' && Number.isFinite(Date.parse(h.nextAttemptAt))) entry.nextAttemptAt = h.nextAttemptAt;
    // #580 5b: the parent-only talk, only as the server named it, and the revision a Talk or a
    // "we talked it over" sends back. Without a readable revision the hold offers neither.
    if (h.talk && (h.talk.state === 'pending' || h.talk.state === 'resolved')) entry.talk = h.talk.state;
    if (typeof h.holdRevision === 'string' && REVISION.test(h.holdRevision)) entry.holdRevision = h.holdRevision;
    return [entry];
  });
}

/**
 * One predicate, used by BOTH the settle payload and the settle total.
 *
 * They were two separate expressions over two different shapes, and they diverged: the total
 * summed every child while the payload excluded the already-settled ones, so the button promised
 * money the request would not send. Sharing the predicate is what makes that impossible rather
 * than merely unlikely.
 */
function isSettleable(kid) {
  return !!kid && !kid.alreadySettled && kid.questsDone > 0 && kid.id != null;
}

/**
 * The user ids a "settle everyone" action would pay.
 *
 * Only children with something eligible AND not already settled, because settling a child twice is
 * the conflict `POST /api/payday/settle` reports per child. A child with no `userId` is dropped
 * for the same reason `toApprovalRows` drops a row with no id: it would name nobody.
 */
export function settleableUserIds(preview) {
  return toPaydayPanel(preview, null).kids.filter(isSettleable).map((kid) => kid.id);
}
